import React, { useState } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Select, MenuItem, FormControl, InputLabel, Snackbar, Alert, Switch, Chip,
    FormControlLabel, Typography, IconButton, Box, Grid
} from '@mui/material';
import {
    PlusCircle, MoreVertical, ExternalLink, Link as LinkIcon, Download,
    RotateCcw, BarChart2, Check, X, Trash2, Edit, Eye
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { useData } from '../hooks/useData';
import { apiFetch } from '../api';
import { PageHeader, LoadingSpinner, ConfirmModal } from '../components/Common';
import { useNotification } from '../context/NotificationContext';
import { DynamicIcon } from '../components/icons';
import { useTimezoneFormatter } from '../hooks/useTimezoneFormatter.js';

export default function DemoManagementPage({ token }) {
    const { data: demoConfigs, isLoading: loadingConfig, refresh } = useData('/tenant-onboarding/demo-configs', token);
    const { data: tenants, isLoading: loadingTenants } = useData('/tenants', token);
    const { addNotification } = useNotification();
    const [isModalVisible, setIsModalVisible] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        tenant_id: '',
        is_active: true,
        ui_title: '',
        ui_desc: ''
    });

    const [editingId, setEditingId] = useState(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [sortOption, setSortOption] = useState('name_asc');

    const activeTenants = (tenants || []).filter(t => t.status === 'Active');

    const handleOpenModal = (record = null) => {
        setEditingId(null);
        if (record) {
            setEditingId(record.id);
            setFormData({
                name: record.name,
                slug: record.slug,
                tenant_id: record.tenant_id,
                is_active: record.is_active,
                ui_title: record.ui_config?.title || '',
                ui_desc: record.ui_config?.description || ''
            });
        } else {
            setFormData({
                name: '',
                slug: '',
                tenant_id: '',
                is_active: true,
                ui_title: '',
                ui_desc: ''
            });
        }
        setIsModalVisible(true);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                name: formData.name,
                slug: formData.slug,
                tenant_id: formData.tenant_id,
                is_active: formData.is_active !== false,
                ui_config: {
                    title: formData.ui_title,
                    description: formData.ui_desc
                }
            };

            if (editingId) {
                await apiFetch(`/tenant-onboarding/demo-configs/${editingId}`, token, {
                    method: 'PUT',
                    body: JSON.stringify(payload)
                });
                addNotification('Demo updated successfully', 'success');
            } else {
                await apiFetch('/tenant-onboarding/demo-configs', token, {
                    method: 'POST',
                    body: JSON.stringify(payload)
                });
                addNotification('Demo created successfully', 'success');
            }
            refresh(true);
            setIsModalVisible(false);
        } catch (error) {
            addNotification(error.message, 'error');
        }
    };

    const handleDelete = async () => {
        if (!confirmDeleteId) return;
        try {
            await apiFetch(`/tenant-onboarding/demo-configs/${confirmDeleteId}`, token, { method: 'DELETE' });
            addNotification('Demo deleted', 'success');
            refresh(true);
        } catch (error) {
            addNotification(error.message, 'error');
        } finally {
            setConfirmDeleteId(null);
        }
    };

    const handleToggleStatus = async (record, checked) => {
        try {
            await apiFetch(`/tenant-onboarding/demo-configs/${record.id}`, token, {
                method: 'PUT',
                body: JSON.stringify({ is_active: checked })
            });
            refresh(true);
        } catch (error) {
            addNotification(error.message, 'error');
        }
    };

    const [insightsModalVisible, setInsightsModalVisible] = useState(false);
    const [selectedDemoForInsights, setSelectedDemoForInsights] = useState(null);
    const sortedDemoConfigs = React.useMemo(() => {
        const list = [...(demoConfigs || [])];
        list.sort((a, b) => {
            if (sortOption === 'name_asc') return (a.name || '').localeCompare(b.name || '');
            if (sortOption === 'name_desc') return (b.name || '').localeCompare(a.name || '');
            if (sortOption === 'status_active') return Number(!!b.is_active) - Number(!!a.is_active);
            if (sortOption === 'status_inactive') return Number(!!a.is_active) - Number(!!b.is_active);
            return 0;
        });
        return list;
    }, [demoConfigs, sortOption]);

    const handleOpenInsights = (record) => {
        setSelectedDemoForInsights(record);
        setInsightsModalVisible(true);
    };

    if (loadingConfig || loadingTenants) return <LoadingSpinner />;

    return (
        <div className="p-6">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <PageHeader title="Demo Management">
                </PageHeader>
                <Box display="flex" gap={1}>
                    <FormControl size="small" sx={{ minWidth: 170, bgcolor: 'background.paper' }}>
                        <InputLabel>Sort</InputLabel>
                        <Select
                            value={sortOption}
                            label="Sort"
                            onChange={(e) => setSortOption(e.target.value)}
                        >
                            <MenuItem value="name_asc">Name A-Z</MenuItem>
                            <MenuItem value="name_desc">Name Z-A</MenuItem>
                            <MenuItem value="status_active">Active First</MenuItem>
                            <MenuItem value="status_inactive">Inactive First</MenuItem>
                        </Select>
                    </FormControl>
                    <Button variant="contained" startIcon={<PlusCircle size={16} />} onClick={() => handleOpenModal()}>
                        Create Demo
                    </Button>
                </Box>
            </Box>

            <Typography variant="body1" color="textSecondary" paragraph>
                Create and manage public landing pages linked to your tenant environments.
            </Typography>

            <TableContainer component={Paper} className="shadow-sm">
                <Table>
                    <TableHead className="bg-gray-50 dark:bg-gray-700">
                        <TableRow>
                            <TableCell>Demo Name</TableCell>
                            <TableCell>Public URL</TableCell>
                            <TableCell>Linked Tenant</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedDemoConfigs?.map((record) => {
                            const tenant = tenants?.find(x => x.id === record.tenant_id);
                            const url = `${window.location.origin}/demo/${record.slug}`;
                            return (
                                <TableRow key={record.id} hover>
                                    <TableCell sx={{ fontWeight: 500 }}>{record.name}</TableCell>
                                    <TableCell>
                                        <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:underline">
                                            /demo/{record.slug} <ExternalLink size={12} />
                                        </a>
                                    </TableCell>
                                    <TableCell>
                                        {tenant ? (
                                            <Chip label={tenant.name} color="primary" variant="outlined" size="small" />
                                        ) : (
                                            <Chip label={`Unknown (${record.tenant_id})`} color="error" size="small" />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={record.is_active}
                                                    onChange={(e) => handleToggleStatus(record, e.target.checked)}
                                                    size="small"
                                                />
                                            }
                                            label={record.is_active ? "Active" : "Disabled"}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box display="flex" justifyContent="flex-end" gap={1}>
                                            <Button size="small" variant="outlined" onClick={() => handleOpenInsights(record)}>Insights</Button>
                                            <Button size="small" variant="outlined" onClick={() => handleOpenModal(record)} withShadow>Edit</Button>
                                            <Button size="small" color="error" onClick={() => setConfirmDeleteId(record.id)}>Delete</Button>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {demoConfigs?.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>No demos found.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create/Edit Dialog */}
            <Dialog open={isModalVisible} onClose={() => setIsModalVisible(false)} maxWidth="sm" fullWidth>
                <form onSubmit={handleSave}>
                    <DialogTitle>{editingId ? "Edit Demo" : "Create New Demo"}</DialogTitle>
                    <DialogContent>
                        <Box display="flex" flexDirection="column" gap={2} pt={1}>
                            <TextField
                                label="Display Name"
                                name="name"
                                value={formData.name}
                                onChange={handleFormChange}
                                required
                                fullWidth
                                placeholder="e.g. Healthcare Q4 Demo"
                            />
                            <TextField
                                label="URL Slug"
                                name="slug"
                                value={formData.slug}
                                onChange={handleFormChange}
                                required
                                fullWidth
                                disabled={!!editingId}
                                InputProps={{
                                    startAdornment: <Typography variant="caption" sx={{ mr: 1 }}>/demo/</Typography>
                                }}
                                placeholder="healthcare"
                            />
                            <FormControl fullWidth required>
                                <InputLabel>Linked Tenant Environment</InputLabel>
                                <Select
                                    name="tenant_id"
                                    value={formData.tenant_id}
                                    onChange={handleFormChange}
                                    label="Linked Tenant Environment"
                                >
                                    {activeTenants.map(t => (
                                        <MenuItem key={t.id} value={t.id}>
                                            {t.name} ({t.planId || 'No Plan'})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Box borderTop={1} borderColor="divider" pt={2} mt={1}>
                                <Typography variant="subtitle2" gutterBottom>Landing Page Config</Typography>
                                <TextField
                                    label="Page Title"
                                    name="ui_title"
                                    value={formData.ui_title}
                                    onChange={handleFormChange}
                                    fullWidth
                                    margin="dense"
                                    placeholder="Welcome to the demo..."
                                />
                                <TextField
                                    label="Description"
                                    name="ui_desc"
                                    value={formData.ui_desc}
                                    onChange={handleFormChange}
                                    fullWidth
                                    multiline
                                    rows={2}
                                    margin="dense"
                                    placeholder="Experience the power of..."
                                />
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setIsModalVisible(false)} withShadow>Cancel</Button>
                        <Button type="submit" variant="contained">Save</Button>
                    </DialogActions>
                </form>
            </Dialog>

            <ConfirmModal
                isOpen={!!confirmDeleteId}
                onClose={() => setConfirmDeleteId(null)}
                onConfirm={handleDelete}
                title="Delete Demo"
            >
                Are you sure you want to delete this demo? This action cannot be undone.
            </ConfirmModal>

            {/* Insights Modal */}
            {selectedDemoForInsights && (
                <DemoInsightsModal
                    token={token}
                    demoConfig={selectedDemoForInsights}
                    visible={insightsModalVisible}
                    onClose={() => setInsightsModalVisible(false)}
                />
            )}
        </div>
    );
}

function DemoInsightsModal({ token, demoConfig, visible, onClose }) {
    const { data, isLoading } = useData(`/tenant-onboarding/demo-configs/${demoConfig.id}/insights`, token);
    const { formatDate } = useTimezoneFormatter();

    const handleExport = () => {
        if (!data || !data.visitors) return;

        const headers = ["Demo Name", "Public URL", "Name", "Email", "Company", "Job Title", "Location", "State", "Country", "Contact Number", "Source", "Date of Registration"];
        const csvRows = [headers.join(",")];

        const demoUrl = `${window.location.origin}/demo/${demoConfig.slug}`;

        data.visitors.forEach(v => {
            const row = [
                demoConfig.name,
                demoUrl,
                v.name,
                v.email,
                v.company,
                v.job_title || "",
                v.location,
                v.state,
                v.country,
                v.contact_number || "",
                v.source || "",
                v.registered_at || ""
            ].map(val => `"${(val || "").replace(/"/g, '""')}"`); // Escape quotes
            csvRows.push(row.join(","));
        });

        const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `demo_visitors_${demoConfig.slug}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    return (
        <Dialog
            open={visible}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { minHeight: 400 } }}
        >
            <DialogTitle display="flex" justifyContent="space-between" alignItems="center">
                Insights: {demoConfig.name}
                <IconButton onClick={onClose} size="small"><X /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {isLoading ? (
                    <LoadingSpinner />
                ) : !data ? (
                    <Typography>No data available</Typography>
                ) : (
                    <div className="space-y-6 pt-2">
                        {/* KPI Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <KPICard title="Total Registrations" value={data.stats.total_registrations} />
                            <KPICard title="Companies" value={data.stats.unique_companies} />
                            <KPICard title="Locations" value={data.stats.unique_locations} />
                            <KPICard title="Countries" value={data.stats.unique_countries} />
                        </div>

                        {/* Trend Chart (New) */}
                        <div className="bg-white dark:bg-gray-800 p-4 rounded border dark:border-gray-700">
                            <h4 className="font-medium mb-4 text-gray-700 dark:text-gray-200">Registration Trend (Last 15 Days)</h4>
                            <div className="h-64 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={data.registration_trend}>
                                        <defs>
                                            <linearGradient id="colorRegs" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                                                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                        <XAxis
                                            dataKey="date"
                                            tickFormatter={(t) => t ? t.substring(5) : ''}
                                            tick={{ fontSize: 12, fill: '#6B7280' }}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fill: '#6B7280' }} />
                                        <RechartsTooltip
                                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                        />
                                        <Area type="monotone" dataKey="count" stroke="#3b82f6" fillOpacity={1} fill="url(#colorRegs)" name="Registrations" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <TopList title="Top Companies" items={data.stats.by_company} />
                            <TopList title="Top Locations" items={data.stats.by_location} />
                        </div>

                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <Typography variant="h6">Visitor Details</Typography>
                                <Button onClick={handleExport} startIcon={<Download size={14} />}>Export CSV</Button>
                            </div>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead className="bg-gray-50 dark:bg-gray-800">
                                        <TableRow>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Company</TableCell>
                                            <TableCell>Location</TableCell>
                                            <TableCell>Registered At</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.visitors.slice(0, 10).map((v, i) => (
                                            <TableRow key={i}>
                                                <TableCell>{v.name}</TableCell>
                                                <TableCell>{v.email}</TableCell>
                                                <TableCell>{v.company}</TableCell>
                                                <TableCell>{[v.location, v.country].filter(Boolean).join(", ")}</TableCell>
                                                <TableCell>{v.registered_at ? formatDate(v.registered_at) : '-'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {data.visitors.length > 10 && <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>Showing top 10 recent visitors. Export to see all.</Typography>}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function KPICard({ title, value }) {
    return (
        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg text-center">
            <div className="text-gray-500 dark:text-gray-400 text-sm uppercase font-semibold">{title}</div>
            <div className="text-2xl font-bold mt-1 text-gray-900 dark:text-white">{value}</div>
        </div>
    );
}

function TopList({ title, items }) {
    return (
        <div className="bg-white dark:bg-gray-800 p-4 rounded border dark:border-gray-700">
            <h4 className="font-medium mb-3 text-gray-700 dark:text-gray-200 border-b pb-2">{title}</h4>
            <ul className="space-y-2">
                {items.map((item, idx) => (
                    <li key={idx} className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-300 truncate pr-2">{item.name || "Unknown"}</span>
                        <span className="font-semibold">{item.count}</span>
                    </li>
                ))}
                {items.length === 0 && <li className="text-gray-400 italic text-sm">No data</li>}
            </ul>
        </div>
    );
}
